import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { CheckCircle, Calendar, Mail } from "lucide-react";
import { useNavigate } from "react-router-dom";
import TagManager from "react-gtm-module";
import { useTranslation } from "react-i18next";

const FreeConsultationInterview = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  useEffect(() => {
    // Track page view with Google Tag Manager
    TagManager.dataLayer({
      dataLayer: {
        event: "page_view",
        page_title: "Free Consultation Confirmed",
        page_location: window.location.href,
      },
    });

    // Parse URL parameters for personalized content
    const params = new URLSearchParams(window.location.search);
    const eventName = params.get('event_type_name') || 'Your Free Consultation';
    const eventStartTime = params.get('event_start_time');
    
    // Update page content if parameters are available
    if (eventName) {
      const eventElement = document.getElementById('event-name');
      if (eventElement) {
        eventElement.textContent = eventName.replace(/%20/g, ' ');
      }
    }

    if (eventStartTime) {
      try {
        const eventDate = new Date(eventStartTime);
        const formattedDate = eventDate.toLocaleDateString(undefined, {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
        const formattedTime = eventDate.toLocaleTimeString(undefined, {
          hour: 'numeric',
          minute: '2-digit'
        });

        const timeElement = document.getElementById('event-time');
        if (timeElement) {
          timeElement.textContent = `${formattedDate} at ${formattedTime}`;
        }
      } catch (error) {
        console.error("Could not parse event date:", error);
      }
    }
  }, []);

  const handleBackToHome = () => {
    navigate("/interview-accelerator");
  };

  const handleBookFreeConsultation = () => {
    window.open(
        "https://calendly.com/bammbuu-languages/career-strategy-english-audit",
        "_blank",
      );
  };

  return (
    <div className="min-h-screen bg-[#F0FDF1] flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-white rounded-3xl shadow-2xl p-8 max-w-2xl w-full text-center"
      >
        {/* Success Icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="flex justify-center mb-6"
        >
          <div className="w-20 h-20 bg-[#E9F7EF] rounded-full flex items-center justify-center">
            <CheckCircle className="w-12 h-12 text-[#14B82C]" />
          </div>
        </motion.div>

        {/* Main Content */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <h1 className="text-4xl font-bold text-[#042F0C] mb-4">
            Success! Your Free Interview Study Guide is Waiting
          </h1>
          
          <p className="text-xl text-[#3D3D3D] mb-8 leading-relaxed">
            You've unlocked a complimentary Business English Interview Study Guide!  Attend your free consultation to claim it. We'll email you shortly to schedule - don't forget to check your spam folder!
          </p>

          {/* Meeting Details Box */}
          <div className="bg-[#F9F9F9] border border-[#E0E0E0] rounded-2xl p-6 mb-8 text-left">
            <div className="flex items-center gap-3 mb-4">
              <Calendar className="w-6 h-6 text-[#14B82C]" />
              <h3 className="text-lg font-semibold text-[#042F0C]">Meeting Details</h3>
            </div>
            <div className="space-y-3">
              <div>
                <strong className="text-[#042F0C]">Event:</strong>
                <span id="event-name" className="text-[#14B82C] font-medium ml-2">
                  Your Free Consultation
                </span>
              </div>
              <div>
                <strong className="text-[#042F0C]">Date & Time:</strong>
                <span id="event-time" className="text-[#14B82C] font-medium ml-2">
                  Please check your email for details.
                </span>
              </div>
            </div>
          </div>

          {/* Next Steps Section */}
          <div className="bg-[#FFFFEA] rounded-2xl p-6 mb-8 text-left">
            <div className="flex items-center gap-3 mb-4">
              <Mail className="w-6 h-6 text-[#FFBF00]" />
              <h2 className="text-xl font-semibold text-[#042F0C]">Next Steps</h2>
            </div>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-[#14B82C] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-sm font-bold">1</span>
                </div>
                <p className="text-[#3D3D3D]">Check your email inbox to accept the calendar invitation</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-[#14B82C] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-sm font-bold">2</span>
                </div>
                <p className="text-[#3D3D3D]">Attend your free consultation call</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-[#14B82C] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-sm font-bold">3</span>
                </div>
                <p className="text-[#3D3D3D]">Receive your personalized study guide</p>
              </div>
            </div>
          </div>

          {/* Back to Home Button */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleBackToHome}
            className="bg-[#14B82C] text-white px-8 mb-3 py-3 rounded-full font-semibold text-lg hover:bg-green-700 transition-colors"
          >
            ← {t("interviewAccelerator.backToInterviewPrep")}
          </motion.button>
           {/* Back to Home Button */}
           <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleBookFreeConsultation}
            className="bg-[#14B82C] text-white ml-4 px-8 py-3 rounded-full font-semibold text-lg hover:bg-green-700 transition-colors"
          >
          {t("interviewAccelerator.bookFreeConsultation")} →
          </motion.button>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default FreeConsultationInterview;
